import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useD502, ItemState } from './hooks/useD502';
import { d502Api } from '../../services/d502Api';
import { BatchAcceptanceBridge, pwd502f1AcceptanceManifest } from '../../features/acceptance';
import './D502Form.css';

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

const D502Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');

  const {
    items,
    loading,
    executeLoading,
    queried,
    lastApiResponse,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleExecute,
  } = useD502();

  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onExecute = useCallback(() => {
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准已勾選的 ${selectedCount} 筆繳庫單？`,
      okText: '確定核准',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  const allSelected =
    items.length > 0 && items.every((s) => s.selected || !s.item.can_approve);

  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected && items.length > 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
          disabled={items.length === 0}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          disabled={!record.item.can_approve}
          onChange={(e) =>
            handleToggleItem(record.item.fd120_01, e.target.checked)
          }
        />
      ),
    },
    {
      title: '繳庫單號',
      dataIndex: ['item', 'fd120_01'],
      key: 'fd120_01',
      width: 120,
    },
    {
      title: '繳庫日期',
      dataIndex: ['item', 'fd120_11'],
      key: 'fd120_11',
      width: 110,
      render: (v: string | null) => fmtDate(v),
    },
    {
      title: '供應商',
      key: 'supplier',
      width: 180,
      render: (_: unknown, record: ItemState) => {
        const code = record.item.fd120_12;
        const name = record.item.supplier_name;
        if (code && name) return `${code} - ${name}`;
        return code || name || '';
      },
    },
    {
      title: '採購單號',
      dataIndex: ['item', 'fd120_21'],
      key: 'fd120_21',
      width: 120,
    },
    {
      title: '庫位',
      dataIndex: ['item', 'fd120_14'],
      key: 'fd120_14',
      width: 100,
    },
  ];

  return (
    <div className="d502-form">
      {(loading || executeLoading) && (
        <div className="loading-overlay">
          {executeLoading ? '核准執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                採購管理
              </a>
              <span>/</span>
              <span className="current">繳庫單核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>繳庫單核准</h1>
            <span className="badge">D502</span>
          </div>
        </div>

        <div className="query-bar">
          <label data-contract-field="fd120_01">繳庫單 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 180 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 180 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading} data-acceptance-action="PWD502-ACT-LIST-PENDING">
            確定範圍
          </Button>
        </div>

        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fd120_01}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入繳庫單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executeLoading}
            loading={executeLoading}
            onClick={onExecute}
            data-acceptance-action="PWD502-ACT-APPROVE"
          >
            核准 ({selectedCount})
          </Button>
          <Button danger size="large" onClick={() => navigate('/erp')}>
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwd502f1AcceptanceManifest}
          records={items.map(state => state.item)}
          selectedKeys={items.filter(state => state.selected).map(state => state.item.fd120_01)}
          keyOf={(item) => item.fd120_01}
          queryExact={async (key) => {
            const result = await d502Api.query(key, key);
            return result.items?.[0] || null;
          }}
          lastApiResponse={lastApiResponse}
          loading={loading || executeLoading}
          onAutoFill={() => {
            const first = items.find(state => state.item.can_approve);
            if (first) handleToggleItem(first.item.fd120_01, true);
          }}
          autoFillDisabled={!items.some(state => state.item.can_approve)}
          autoFillReason="只選取正式 API 查到的第一筆可核准繳庫單；核准仍走原批次 API。"
          screenDraft={{ start_no: startNo, end_no: endNo, selected_items: items.filter(state => state.selected) }}
        />
      </div>
    </div>
  );
};

export default D502Form;
